// Shared current-selection lifecycle for template media (F3 — see
// docs/specs/2026-09-10-template-media-validation/spec.md). Owns
// validation, preview URL cleanup and one in-flight submission attempt so
// every consumer (settings, Excel) gets the same race guards.
import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";

import {
  validateTemplateMedia,
  TemplateMediaValidationError,
  TEMPLATE_MEDIA_ERROR_CODES,
} from "../utils/templateMediaValidation";

export const TEMPLATE_MEDIA_STATUS = {
  EMPTY: "empty",
  PENDING: "pending",
  VALID: "valid",
  INVALID: "invalid",
  SUBMITTING: "submitting",
};

const DEFAULT_ERROR = {
  code: "UNEXPECTED_ERROR",
  message: "No pudimos procesar el archivo. Vuelve a seleccionarlo.",
};

const toErrorState = (thrown) => ({
  code: thrown?.code || DEFAULT_ERROR.code,
  message: thrown?.message || DEFAULT_ERROR.message,
});

// Work (upload) failures must never surface raw transport text (axios
// errors also carry a code and an English message) and must never repeat the
// re-pick copy above, since a work failure keeps the sample VALID for retry.
// Only our own error class passes its code and Spanish message through.
const DEFAULT_WORK_ERROR = {
  code: "WORK_FAILED",
  message: "No pudimos completar el envío del archivo. Vuelve a intentarlo.",
};

const toWorkErrorState = (thrown) =>
  thrown instanceof TemplateMediaValidationError
    ? { code: thrown.code, message: thrown.message }
    : DEFAULT_WORK_ERROR;

// Safe breadcrumb only: entry-point/stage/format/result code, never
// filenames, URLs or file contents (F4).
const emitBreadcrumb = (entryPoint, stage, format, code) => {
  Sentry.addBreadcrumb({
    category: "template-media",
    message: `${entryPoint}.${stage}.${format || "unknown"}.${code || "ok"}`,
    level: "info",
  });
};

const KNOWN_REJECTION_CODES = new Set(Object.values(TEMPLATE_MEDIA_ERROR_CODES));

// Breadcrumbs only travel with a captured event, so a rejection is also sent
// as an info message tagged by a bounded validator code (never free text).
const reportRejection = (entryPoint, code) => {
  const boundedCode = KNOWN_REJECTION_CODES.has(code) ? code : DEFAULT_ERROR.code;
  Sentry.captureMessage("template-media rejected", {
    level: "info",
    tags: { component: "template-media", entryPoint, code: boundedCode },
    fingerprint: ["template-media-rejected", boundedCode],
  });
};

const useTemplateMediaSelection = ({ context } = {}) => {
  const [status, setStatus] = useState(TEMPLATE_MEDIA_STATUS.EMPTY);
  const [sample, setSample] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);
  const generationRef = useRef(0);
  const contextRef = useRef(context);
  const previewUrlRef = useRef(null);
  const submittingRef = useRef(false);

  // Kept fresh on every render (not only inside a passive effect) so
  // continuations reading `contextRef.current` never lag a prop change.
  // Only a provided prop is the source of truth; consumers that drive the
  // context through `setContext` keep their value across re-renders.
  if (context !== undefined) contextRef.current = context;

  useEffect(
    () => () => {
      mountedRef.current = false;
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    []
  );

  const discardCurrent = useCallback(() => {
    generationRef.current += 1;
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
    setSample(null);
    setError(null);
  }, []);

  const clear = useCallback(() => {
    discardCurrent();
    setStatus(TEMPLATE_MEDIA_STATUS.EMPTY);
  }, [discardCurrent]);

  const setContext = useCallback(
    (nextContext) => {
      contextRef.current = nextContext;
      discardCurrent();
      setStatus(TEMPLATE_MEDIA_STATUS.EMPTY);
    },
    [discardCurrent]
  );

  const select = useCallback(
    (file) => {
      discardCurrent();
      setStatus(TEMPLATE_MEDIA_STATUS.PENDING);
      const generation = generationRef.current;
      const activeContext = contextRef.current;
      const entryPoint = activeContext?.entryPoint || "settings";

      validateTemplateMedia(file, activeContext?.expectedCategory)
        .then((result) => {
          if (!mountedRef.current || generationRef.current !== generation) return;
          previewUrlRef.current = URL.createObjectURL(result.file);
          setSample(result);
          setPreviewUrl(previewUrlRef.current);
          setStatus(TEMPLATE_MEDIA_STATUS.VALID);
          emitBreadcrumb(entryPoint, "select", result.format, "valid");
        })
        .catch((validationError) => {
          if (!mountedRef.current || generationRef.current !== generation) return;
          const errorState = toErrorState(validationError);
          setError(errorState);
          setStatus(TEMPLATE_MEDIA_STATUS.INVALID);
          emitBreadcrumb(entryPoint, "select", null, errorState.code);
          reportRejection(entryPoint, errorState.code);
        });
    },
    [discardCurrent]
  );

  const runCurrent = useCallback(
    async (work) => {
      if (
        status !== TEMPLATE_MEDIA_STATUS.VALID ||
        !sample ||
        submittingRef.current
      )
        return { started: false };

      submittingRef.current = true;
      setError(null);
      const generation = generationRef.current;
      const snapshotContext = contextRef.current;
      const entryPoint = snapshotContext?.entryPoint || "settings";
      setStatus(TEMPLATE_MEDIA_STATUS.SUBMITTING);

      const isCurrent = () =>
        mountedRef.current &&
        generationRef.current === generation &&
        contextRef.current === snapshotContext;

      try {
        const result = await work(sample, isCurrent);
        if (isCurrent()) setStatus(TEMPLATE_MEDIA_STATUS.VALID);
        emitBreadcrumb(entryPoint, "submit", sample.format, "ok");
        return { started: true, ok: true, result };
      } catch (workError) {
        const errorState = toWorkErrorState(workError);
        if (!(workError instanceof TemplateMediaValidationError))
          Sentry.captureException(workError, {
            tags: { component: "template-media", op: "submit" },
          });
        // A work failure (upload) doesn't invalidate an already
        // validated sample: keep VALID so a retry can run without re-picking.
        if (isCurrent()) {
          setError(errorState);
          setStatus(TEMPLATE_MEDIA_STATUS.VALID);
        }
        emitBreadcrumb(entryPoint, "submit", sample.format, errorState.code);
        return { started: true, ok: false, error: errorState };
      } finally {
        // This ref is a single-slot lock: duplicate calls are rejected above
        // before it is ever set, so the attempt releasing it always owns it.
        submittingRef.current = false;
      }
    },
    [status, sample]
  );

  return { status, sample, previewUrl, error, select, clear, setContext, runCurrent };
};

export default useTemplateMediaSelection;
